import { addSearchParams } from '../functions/url-utils'
import { settingsStore } from '../factories/database/settings-store'
import {
  QRCODE_OPTIONS,
  QR_CODE_URL_LENGTH_THRESHOLD,
} from '../components/receipts/selected-receipts.constant'

export interface WebPresenceItem {
  id: string
  link_type: string
  url: string
  message?: string
  show_qr_code: boolean
  is_enabled?: boolean
}

export interface RenderedWebPresenceItem {
  Message: string
  URL: string
  DisplayURL: string
  QRCode: string | null
}

const E_SERIES_LINK_TYPES = new Set([
  'ls_ecom',
  'showroom',
  'customer_portal',
  'linkup',
])

const QR_CACHE_KEY_PREFIX = 'web_presence_qr:'

const memoryQrCache = new Map<string, string>()

function isESeriesLinkType(linkType: string): boolean {
  return E_SERIES_LINK_TYPES.has(linkType)
}

function buildQrUtmUrl(url: string, outletName: string): string {
  const parsed = new URL(url)
  return addSearchParams(parsed, {
    utm_source: 'receipt',
    utm_medium: 'offline',
    utm_campaign: 'post_purchase',
    utm_content: 'qr',
    utm_term: outletName,
  }).toString()
}

export function qrUrlForItem(
  item: WebPresenceItem,
  outletName: string
): string {
  if (!isESeriesLinkType(item.link_type)) {
    return item.url
  }
  try {
    return buildQrUtmUrl(item.url, outletName)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[web-presence-service] Failed to build QR UTM URL', e)
    return item.url
  }
}

function persistenceKey(cacheKey: string): string {
  return `${QR_CACHE_KEY_PREFIX}${cacheKey}`
}

async function fetchQrAsBase64(
  url: string,
  size: number = QRCODE_OPTIONS.defaultSize
): Promise<string> {
  const qrServiceUrl = addSearchParams(
    new URL('/images/qrcode', window.location.origin),
    { content: url, size }
  )
  const response = await fetch(qrServiceUrl.toString())
  if (!response.ok) {
    throw new Error(`QR fetch failed: ${response.status}`)
  }
  const blob = await response.blob()
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function loadFromPersistence(cacheKey: string): Promise<string | null> {
  const persisted = await settingsStore.get(persistenceKey(cacheKey))
  return persisted ? (persisted as string) : null
}

async function persistToStorage(
  cacheKey: string,
  base64: string
): Promise<void> {
  await settingsStore.put(persistenceKey(cacheKey), base64)
}

export function getQrFromMemoryCache(
  url: string,
  size: number = QRCODE_OPTIONS.defaultSize
): string | null {
  return memoryQrCache.get(`${url}::${size}`) ?? null
}

export async function preCacheQrForItems(
  items: WebPresenceItem[],
  outletName: string
): Promise<void> {
  const itemsWithQr = items.filter(item => item.show_qr_code)

  await Promise.all(
    itemsWithQr.map(async item => {
      const url = qrUrlForItem(item, outletName)
      const size =
        url.length > QR_CODE_URL_LENGTH_THRESHOLD
          ? QRCODE_OPTIONS.largeSize
          : QRCODE_OPTIONS.defaultSize
      const cacheKey = `${url}::${size}`

      if (memoryQrCache.has(cacheKey)) {
        return
      }

      const persisted = await loadFromPersistence(cacheKey)
      if (persisted) {
        memoryQrCache.set(cacheKey, persisted)
        return
      }

      try {
        const base64 = await fetchQrAsBase64(url, size)
        memoryQrCache.set(cacheKey, base64)
        await persistToStorage(cacheKey, base64)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[web-presence-service] Failed to pre-cache QR code', e)
      }
    })
  )
}

export function buildWebPresenceItems(
  items: WebPresenceItem[],
  outletName: string
): RenderedWebPresenceItem[] {
  return items.map(item => {
    const url = qrUrlForItem(item, outletName)
    const size =
      url.length > QR_CODE_URL_LENGTH_THRESHOLD
        ? QRCODE_OPTIONS.largeSize
        : QRCODE_OPTIONS.defaultSize
    return {
      Message: item.message ?? '',
      URL: item.url,
      DisplayURL: item.url,
      QRCode: item.show_qr_code ? getQrFromMemoryCache(url, size) : null,
    }
  })
}
